import { FieldType } from '@databases/model/field';
import {
  type AttachmentValue,
  type MutableAttachmentValue,
  type SingleAttachmentValue,
  type ValueJSON,
} from '@databases/model/value';
import * as Y from 'yjs';

import { SynchronizedArray } from '../helpers/synchronized-array';
import { SynchronizedMapElement } from '../helpers/synchronized-map-element';
import { SynchronizedRecord } from '../helpers/synchronized-record';
import { type DisposableElement, type WrappedYType } from '../helpers/types';
import { ValueProperties } from '../y-entry';
import { FieldMapping } from '../y-field';

const AttachmentValueProperties = {
  fileId: 'f',
  id: 'i',
};

export class YSingleAttachmentValue extends SynchronizedRecord {
  static mapper(value: unknown): YSingleAttachmentValue | undefined {
    if (value instanceof Y.Map) {
      return new YSingleAttachmentValue(value);
    }
  }

  get fileId(): string {
    return this.getString(AttachmentValueProperties.fileId, '');
  }

  set fileId(val: string) {
    this.set(AttachmentValueProperties.fileId, val);
  }

  get id(): string {
    return this.getString(AttachmentValueProperties.id, '');
  }

  toJSON(): SingleAttachmentValue {
    return {
      fileId: this.fileId,
      id: this.id,
    };
  }

  static fromJSON(value: SingleAttachmentValue): YSingleAttachmentValue {
    return new YSingleAttachmentValue(
      new Y.Map([
        [AttachmentValueProperties.fileId, value.fileId],
        [AttachmentValueProperties.id, value.id],
      ])
    );
  }
}
export class YAttachmentValue
  implements AttachmentValue, MutableAttachmentValue, WrappedYType, DisposableElement
{
  private valueElement: SynchronizedMapElement<SynchronizedArray<YSingleAttachmentValue>>;

  constructor(private input: Y.Map<any>) {
    this.valueElement = this.createValueElement();
  }

  private createValueElement() {
    return new SynchronizedMapElement<SynchronizedArray<YSingleAttachmentValue>>(
      this.input,
      ValueProperties.value,
      (yarray) => {
        if (yarray instanceof Y.Array) {
          return new SynchronizedArray(
            yarray as Y.Array<unknown>,
            YSingleAttachmentValue.mapper
          );
        }
      },
      () => new Y.Array()
    );
  }

  get type(): FieldType.Attachment {
    return FieldType.Attachment;
  }

  get isEmpty() {
    return !this.valueElement.value?.toArray().length;
  }

  get value() {
    return this.valueElement.value?.toArray() ?? [];
  }

  add(value: SingleAttachmentValue): void {
    this.valueElement.valueRequired.push([YSingleAttachmentValue.fromJSON(value)]);
  }

  delete(index: number): void {
    this.valueElement.valueRequired.delete(index);
  }

  reorder(index: number, newIndex: number): void {
    const val = this.valueElement.valueRequired.get(index).toJSON();
    this.valueElement.valueRequired.delete(index);
    this.valueElement.valueRequired.insert(newIndex, [
      YSingleAttachmentValue.fromJSON(val),
    ]);
  }

  setTo(values: SingleAttachmentValue[]) {
    const valueRef = new Y.Array();
    this.input.set(ValueProperties.value, valueRef);
    this.valueElement = this.createValueElement();

    this.valueElement.valueRequired.push(
      values.map((v) => YSingleAttachmentValue.fromJSON(v))
    );
  }

  toJSON(): ValueJSON<FieldType.Attachment, SingleAttachmentValue[]> {
    return {
      type: this.type,
      value: this.value.map((v) => v.toJSON()),
    };
  }

  static fromJSON(json: ValueJSON<FieldType.Attachment, SingleAttachmentValue[]>) {
    return new YAttachmentValue(
      new Y.Map([
        [ValueProperties.type, FieldMapping[json.type]],
        [
          ValueProperties.value,
          Y.Array.from(
            json.value.map((v) => YSingleAttachmentValue.fromJSON(v).unwrap())
          ),
        ],
      ])
    );
  }

  unwrap(): Y.AbstractType<any> {
    return this.input;
  }

  dispose(): void {
    this.valueElement.dispose();
  }
}
